import debounce from 'lodash/debounce'
import React, { FC, useCallback, useEffect, useRef, useState } from 'react'

import {
  DatePickerField,
  DatePickerFieldProps,
  FilterAccordion,
  formatDate,
  getDatesFromPeriod,
  useOnClickOutside,
} from '@avantstay/backoffice-core'
import { formatDateRange } from '@avantstay/format-date-range'

import { FiltersSharedProps } from '../Filters.types'

interface DatePickerBasedFilterProps
  extends FiltersSharedProps,
    Omit<DatePickerFieldProps, 'dateValue'> {
  title: string
  initialValue?: string
  onChange: (value?: string) => void
}

export const DatePickerBasedFilter: FC<DatePickerBasedFilterProps> = ({
  title,
  initialValue,
  isSidebarFilter,
  onChange,
  isAccordionOpen,
  ...datePickerProps
}) => {
  const [localValue, setLocalValue] = useState<string | undefined>(initialValue)

  const wrapperRef = useRef<HTMLDivElement>(null)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedInputOnChange = useCallback(
    debounce((value?: string) => {
      onChange(value)
    }, 300),
    [],
  )

  const handleSubmitValue = () => {
    if (localValue !== initialValue) {
      debouncedInputOnChange(localValue)
    }
  }

  useOnClickOutside(wrapperRef, handleSubmitValue)

  useEffect(() => {
    setLocalValue(initialValue)
  }, [initialValue])

  useEffect(() => {
    if (isSidebarFilter) {
      handleSubmitValue()
    }
    onChange(localValue)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localValue, isSidebarFilter])

  // This clears selection when we run propertiesListStore.resetFilters
  useEffect(() => {
    if (initialValue === undefined) {
      setLocalValue(undefined)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValue])

  const handleOnClear = () => {
    onChange('')
    setLocalValue('')
  }

  const { fromDate, toDate } = getDatesFromPeriod(localValue)

  const filterValue =
    fromDate !== toDate ? formatDateRange(fromDate, toDate) : formatDate(fromDate, 'dd MMM, yyyy')

  return (
    <FilterAccordion
      isAccordionOpen={isAccordionOpen}
      title={title}
      onClearFilter={handleOnClear}
      filterValue={localValue && filterValue}
      ref={wrapperRef}
      isSidebarFilter={isSidebarFilter}
      isDateRange
      shouldShowClearButton={!!isSidebarFilter}
    >
      <DatePickerField.Small
        {...datePickerProps}
        dateValue={localValue || ''}
        onChange={setLocalValue}
      />
    </FilterAccordion>
  )
}
